import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../../api/api";
import {
  Button,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Menu,
  message,
  Modal,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import { AuditItemStatus, IAuditItem } from "../../types/auditItem.type";
import { DownOutlined } from "@ant-design/icons";
import { AuditType } from "../../types/audit.type";

const { Title } = Typography;
const { Search } = Input;

export default function AuditItems() {
  const { id } = useParams();
  const [items, setItems] = useState<IAuditItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<AuditItemStatus | undefined>(undefined);
  const [selectedItem, setSelectedItem] = useState<IAuditItem | undefined>(
    undefined
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/audit/${id}/items`, {
        params: {
          page,
          limit: pageSize,
          search: searchTerm,
          status: status,
        },
      });

      setItems(response.data.items);
      setTotal(response.data.total);
    } catch (error) {
      console.error(error);
      message.error("Ошибка получение данных ревизии");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [page, pageSize, searchTerm, status]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleOpen = (item: IAuditItem) => {
    setSelectedItem(item);
    setIsModalOpen(true);
    form.setFieldsValue(item);
  };

  const handleSubmit = async (values: any) => {
    setConfirmLoading(true);
    try {
      await api.post(`/audit/item/${selectedItem?._id}`, values);
      message.success("Кол-во успешно изменено");
    } catch (error: any) {
      console.error("Error saving item:", error);
      message.error(error.response.data.error);
    } finally {
      setConfirmLoading(false);
      setIsModalOpen(false);
      fetchItems();
      form.resetFields();
    }
  };

  const columns = [
    {
      title: "Имя",
      dataIndex: "itemName",
      key: "itemName",
      fixed: "left" as "left",
    },
    {
      title: "Цена USD",
      dataIndex: "price",
      key: "price.USD",
      render: (price: { USD: number; UZS: number }) => price.USD,
    },
    {
      title: "Цена UZS",
      dataIndex: "price",
      key: "price.UZS",
      render: (price: { USD: number; UZS: number }) => price.UZS,
    },
    {
      title: "Количество",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "Количество КГ",
      dataIndex: "quantityKg",
      key: "quantityKg",
    },
    {
      title: "Факт.Количество",
      dataIndex: "actualQuantity",
      key: "actualQuantity",
    },
    {
      title: "Разница Количество",
      dataIndex: "quantityDifference",
      key: "quantityDifference",
      render: (_: any, item: IAuditItem) => item.quantity - item.actualQuantity,
    },
    {
      title: "Факт.Количество КГ",
      dataIndex: "actualQuantityKg",
      key: "actualQuantityKg",
    },
    {
      title: "Разница USD",
      dataIndex: "difference",
      key: "difference.USD",
      render: (difference: { USD: number; UZS: number }) => difference.USD,
    },
    {
      title: "Разница UZS",
      dataIndex: "difference",
      key: "difference.UZS",
      render: (difference: { USD: number; UZS: number }) => difference.UZS,
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "",
      key: "actions",
      render: (_: any, item: IAuditItem) => {
        const menu = (
          <Menu>
            <Menu.Item onClick={() => handleOpen(item)}>
              Изменить кол-во
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const rowClassName = (record: IAuditItem) => {
    if (record.quantity < record.actualQuantity) {
      return "row-green";
    } else if (record.quantity > record.actualQuantity) {
      return "row-red";
    }
    return "";
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Данные ревизии</Title>
      <Space style={{ marginBottom: 16 }}>
        <Search
          placeholder="Поиск по названию"
          onSearch={handleSearch}
          allowClear
        />
        <Select
          placeholder="Фильтр по статусу"
          onChange={(value) => setStatus(value)}
          allowClear
          style={{ width: 200 }}
        >
          {Object.values(AuditItemStatus).map((status) => (
            <Select.Option key={status} value={status}>
              {status}
            </Select.Option>
          ))}
        </Select>
      </Space>

      <Table
        columns={columns}
        dataSource={items}
        rowKey={(record) => record._id}
        loading={loading}
        rowClassName={rowClassName}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
        }}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title="Изменить"
        open={isModalOpen}
        confirmLoading={confirmLoading}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="quantity" label="Количество">
            <InputNumber disabled />
          </Form.Item>
          <Form.Item name="actualQuantity" label="Факт.Количество">
            <InputNumber />
          </Form.Item>

          {selectedItem?.type === AuditType.PRODUCE && (
            <>
              <Form.Item name="quantityKg" label="Количество КГ">
                <InputNumber disabled />
              </Form.Item>
              <Form.Item name="actualQuantityKg" label="Факт.Количество КГ">
                <InputNumber />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
}
